import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import '../mortgage-reconciliation.css';

type Props = {
  userId: string;
};

type MortgageAccount = {
  id: string;
  lender: string;
  product_name: string;
  current_projected_balance: number | null;
  balance_as_of: string | null;
  annual_interest_rate: number;
};

type Installment = {
  id: string;
  period_number: number;
  due_date: string;
  scheduled_total: number;
  actual_charge: number | null;
  charge_status: string;
};

type Reconciliation = {
  id: string;
  statement_on: string;
  period_label: string | null;
  reported_balance: number;
  scheduled_balance: number | null;
  next_due_on: string | null;
  next_payment_total: number | null;
  annual_interest_rate: number | null;
  principal_paid_period: number | null;
  interest_paid_period: number | null;
  prepayments_reported: number | null;
  document_path: string | null;
  document_name: string | null;
  status: 'draft' | 'applied' | 'superseded';
  notes: string | null;
  applied_at: string | null;
};

type ScheduleVersion = {
  id: string;
  version_number: number;
  effective_on: string;
  label: string;
  projected_balance: number | null;
  is_current: boolean;
};

type FormState = {
  statementOn: string;
  periodLabel: string;
  reportedBalance: string;
  scheduledBalance: string;
  nextDueOn: string;
  nextPaymentTotal: string;
  annualInterestRate: string;
  lifeInsurance: string;
  propertyInsurance: string;
  principalPaid: string;
  interestPaid: string;
  prepaymentsReported: string;
  notes: string;
};

const currency = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat('es-MX', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

function today() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(value: string | null) {
  if (!value) return 'Pendiente';
  return dateFormatter.format(new Date(`${value}T00:00:00Z`));
}

function nullableNumber(value: string) {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function safeFileName(name: string) {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 100);
}

function makeDefaultForm(account: MortgageAccount | null, installment: Installment | null): FormState {
  return {
    statementOn: today(),
    periodLabel: '',
    reportedBalance: account?.current_projected_balance != null ? String(account.current_projected_balance) : '',
    scheduledBalance: account?.current_projected_balance != null ? String(account.current_projected_balance) : '',
    nextDueOn: installment?.due_date ?? '',
    nextPaymentTotal: installment ? String(installment.actual_charge ?? installment.scheduled_total ?? '') : '',
    annualInterestRate: account?.annual_interest_rate != null ? String(account.annual_interest_rate) : '',
    lifeInsurance: '',
    propertyInsurance: '',
    principalPaid: '',
    interestPaid: '',
    prepaymentsReported: '',
    notes: '',
  };
}

function statusLabel(status: Reconciliation['status']) {
  if (status === 'applied') return 'Aplicada';
  if (status === 'superseded') return 'Sustituida';
  return 'Borrador';
}

export default function MortgageReconciliation({ userId }: Props) {
  const [mortgage, setMortgage] = useState<MortgageAccount | null>(null);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [reconciliations, setReconciliations] = useState<Reconciliation[]>([]);
  const [versions, setVersions] = useState<ScheduleVersion[]>([]);
  const [form, setForm] = useState<FormState>(() => makeDefaultForm(null, null));
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [initialized, setInitialized] = useState(false);

  async function loadData() {
    setLoading(true);
    setMessage('');

    const [mortgageResult, installmentsResult, reconciliationsResult, versionsResult] = await Promise.all([
      supabase
        .from('mortgage_accounts')
        .select('id, lender, product_name, current_projected_balance, balance_as_of, annual_interest_rate')
        .eq('status', 'active')
        .limit(1)
        .maybeSingle(),
      supabase
        .from('mortgage_installments_public')
        .select('id, period_number, due_date, scheduled_total, actual_charge, charge_status')
        .order('due_date', { ascending: true })
        .limit(12),
      supabase
        .from('mortgage_reconciliations')
        .select('id, statement_on, period_label, reported_balance, scheduled_balance, next_due_on, next_payment_total, annual_interest_rate, principal_paid_period, interest_paid_period, prepayments_reported, document_path, document_name, status, notes, applied_at')
        .order('statement_on', { ascending: false })
        .order('created_at', { ascending: false }),
      supabase
        .from('mortgage_schedule_versions')
        .select('id, version_number, effective_on, label, projected_balance, is_current')
        .order('version_number', { ascending: false }),
    ]);

    const firstError = [
      mortgageResult.error,
      installmentsResult.error,
      reconciliationsResult.error,
      versionsResult.error,
    ].find(Boolean);

    if (firstError) throw firstError;

    const account = (mortgageResult.data ?? null) as MortgageAccount | null;
    const nextInstallments = (installmentsResult.data ?? []) as Installment[];

    setMortgage(account);
    setInstallments(nextInstallments);
    setReconciliations((reconciliationsResult.data ?? []) as Reconciliation[]);
    setVersions((versionsResult.data ?? []) as ScheduleVersion[]);

    if (!initialized) {
      const nextScheduled = nextInstallments.find((item) => item.charge_status === 'scheduled') ?? nextInstallments[0] ?? null;
      setForm(makeDefaultForm(account, nextScheduled));
      setInitialized(true);
    }

    setLoading(false);
  }

  useEffect(() => {
    void loadData().catch(() => {
      setLoading(false);
      setMessage('No fue posible cargar la información hipotecaria.');
    });
  }, []);

  const variance = useMemo(() => {
    const reported = Number(form.reportedBalance);
    const scheduled = Number(form.scheduledBalance);
    if (!Number.isFinite(reported) || !Number.isFinite(scheduled)) return null;
    return reported - scheduled;
  }, [form.reportedBalance, form.scheduledBalance]);

  const nextScheduled = installments.find((item) => item.charge_status === 'scheduled') ?? installments[0] ?? null;
  const latestApplied = reconciliations.find((item) => item.status === 'applied') ?? null;
  const currentVersion = versions.find((item) => item.is_current) ?? versions[0] ?? null;
  const draftCount = reconciliations.filter((item) => item.status === 'draft').length;

  async function submitReconciliation(event: FormEvent) {
    event.preventDefault();
    if (!mortgage) return;

    const reportedBalance = Number(form.reportedBalance);
    if (!Number.isFinite(reportedBalance) || reportedBalance < 0) {
      setMessage('Ingresa un saldo bancario válido.');
      return;
    }

    if (documentFile) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(documentFile.type)) {
        setMessage('El documento debe ser PDF, JPG, PNG o WEBP.');
        return;
      }
      if (documentFile.size > 10 * 1024 * 1024) {
        setMessage('El documento no puede exceder 10 MB.');
        return;
      }
    }

    setBusy(true);
    setMessage('');

    const { data, error } = await supabase
      .from('mortgage_reconciliations')
      .insert({
        mortgage_id: mortgage.id,
        statement_on: form.statementOn,
        period_label: form.periodLabel.trim() || null,
        reported_balance: reportedBalance,
        scheduled_balance: nullableNumber(form.scheduledBalance),
        next_due_on: form.nextDueOn || null,
        next_payment_total: nullableNumber(form.nextPaymentTotal),
        annual_interest_rate: nullableNumber(form.annualInterestRate),
        life_insurance: nullableNumber(form.lifeInsurance),
        property_insurance: nullableNumber(form.propertyInsurance),
        principal_paid_period: nullableNumber(form.principalPaid),
        interest_paid_period: nullableNumber(form.interestPaid),
        prepayments_reported: nullableNumber(form.prepaymentsReported),
        notes: form.notes.trim() || null,
        status: 'draft',
        created_by: userId,
      })
      .select('id')
      .single();

    if (error || !data) {
      setBusy(false);
      setMessage('No fue posible guardar la conciliación.');
      return;
    }

    let documentWarning = '';

    if (documentFile) {
      const path = `${userId}/${data.id}/${Date.now()}-${safeFileName(documentFile.name)}`;
      const upload = await supabase.storage
        .from('mortgage-statements')
        .upload(path, documentFile, {
          cacheControl: '3600',
          contentType: documentFile.type,
          upsert: false,
        });

      if (upload.error) {
        documentWarning = ' La conciliación se guardó, pero el documento no pudo adjuntarse.';
      } else {
        const update = await supabase
          .from('mortgage_reconciliations')
          .update({
            document_path: path,
            document_name: documentFile.name,
            document_size: documentFile.size,
            updated_at: new Date().toISOString(),
          })
          .eq('id', data.id);

        if (update.error) {
          documentWarning = ' El archivo se cargó, pero no pudo vincularse al registro.';
        }
      }
    }

    setDocumentFile(null);
    setFileInputKey((value) => value + 1);
    setMessage(`Conciliación guardada como borrador.${documentWarning}`);
    setInitialized(false);
    await loadData();
    setBusy(false);
  }

  async function applyReconciliation(item: Reconciliation) {
    const confirmed = window.confirm(
      `Aplicar el estado de cuenta del ${formatDate(item.statement_on)} actualizará el saldo visible y creará una nueva versión de la tabla. ¿Continuar?`,
    );
    if (!confirmed) return;

    setBusy(true);
    setMessage('');

    const { error } = await supabase.rpc('apply_mortgage_reconciliation', {
      p_reconciliation_id: item.id,
    });

    if (error) {
      setMessage('No fue posible aplicar la conciliación. Verifica que siga en borrador.');
    } else {
      setMessage('Conciliación aplicada. El saldo y la versión hipotecaria fueron actualizados.');
      setInitialized(false);
      await loadData();
    }

    setBusy(false);
  }

  async function openDocument(item: Reconciliation) {
    if (!item.document_path) return;

    const { data, error } = await supabase.storage
      .from('mortgage-statements')
      .createSignedUrl(item.document_path, 120);

    if (error || !data?.signedUrl) {
      setMessage('No fue posible abrir el documento privado.');
      return;
    }

    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  }

  if (loading) return <section className="recon-state">Cargando conciliación hipotecaria…</section>;
  if (!mortgage) return <section className="recon-state recon-state--error">No se encontró un crédito hipotecario activo.</section>;

  return (
    <section className="recon-page">
      <div className="recon-heading">
        <div>
          <p>HSBC · control documental</p>
          <h1>Conciliación hipotecaria</h1>
          <span>Actualiza el saldo con estados de cuenta sin borrar el historial ni exponer documentos al portal acreedor.</span>
        </div>
        <div className="recon-heading-badge">
          <small>Versión vigente</small>
          <strong>{currentVersion ? `V${currentVersion.version_number}` : 'Sin versión'}</strong>
          <span>{currentVersion ? formatDate(currentVersion.effective_on) : 'Pendiente'}</span>
        </div>
      </div>

      <section className="recon-summary">
        <article>
          <span>Saldo conciliado</span>
          <strong>{currency.format(Number(mortgage.current_projected_balance ?? 0))}</strong>
          <small>Al {formatDate(mortgage.balance_as_of)}</small>
        </article>
        <article>
          <span>Próxima mensualidad</span>
          <strong>{currency.format(Number(nextScheduled?.actual_charge ?? nextScheduled?.scheduled_total ?? 0))}</strong>
          <small>{formatDate(nextScheduled?.due_date ?? null)}</small>
        </article>
        <article>
          <span>Último estado aplicado</span>
          <strong>{latestApplied ? formatDate(latestApplied.statement_on) : 'Pendiente'}</strong>
          <small>{latestApplied?.period_label ?? 'Sin conciliaciones aplicadas'}</small>
        </article>
        <article>
          <span>Borradores pendientes</span>
          <strong>{draftCount}</strong>
          <small>Requieren revisión antes de afectar saldos</small>
        </article>
      </section>

      <div className="recon-layout">
        <form className="recon-form" onSubmit={submitReconciliation}>
          <div className="recon-card-heading">
            <div><small>Nuevo corte bancario</small><h2>Registrar estado de cuenta</h2></div>
            <span>Se guarda primero como borrador</span>
          </div>

          <div className="recon-grid">
            <label>Fecha del estado<input type="date" value={form.statementOn} onChange={(event) => setForm({ ...form, statementOn: event.target.value })} required /></label>
            <label>Etiqueta del periodo<input value={form.periodLabel} onChange={(event) => setForm({ ...form, periodLabel: event.target.value })} placeholder="Ej. Estado agosto 2026" /></label>
            <label>Saldo reportado por HSBC<input type="number" min="0" step="0.01" value={form.reportedBalance} onChange={(event) => setForm({ ...form, reportedBalance: event.target.value })} required /></label>
            <label>Saldo programado previo<input type="number" min="0" step="0.01" value={form.scheduledBalance} onChange={(event) => setForm({ ...form, scheduledBalance: event.target.value })} /></label>
            <label>Próxima fecha de pago<input type="date" value={form.nextDueOn} onChange={(event) => setForm({ ...form, nextDueOn: event.target.value })} /></label>
            <label>Próximo pago total<input type="number" min="0" step="0.01" value={form.nextPaymentTotal} onChange={(event) => setForm({ ...form, nextPaymentTotal: event.target.value })} /></label>
            <label>Tasa anual<input type="number" min="0" step="0.0001" value={form.annualInterestRate} onChange={(event) => setForm({ ...form, annualInterestRate: event.target.value })} /></label>
            <label>Seguro de vida<input type="number" min="0" step="0.01" value={form.lifeInsurance} onChange={(event) => setForm({ ...form, lifeInsurance: event.target.value })} /></label>
            <label>Seguro de daños<input type="number" min="0" step="0.01" value={form.propertyInsurance} onChange={(event) => setForm({ ...form, propertyInsurance: event.target.value })} /></label>
            <label>Capital pagado en el periodo<input type="number" min="0" step="0.01" value={form.principalPaid} onChange={(event) => setForm({ ...form, principalPaid: event.target.value })} /></label>
            <label>Intereses del periodo<input type="number" min="0" step="0.01" value={form.interestPaid} onChange={(event) => setForm({ ...form, interestPaid: event.target.value })} /></label>
            <label>Abonos a capital reportados<input type="number" min="0" step="0.01" value={form.prepaymentsReported} onChange={(event) => setForm({ ...form, prepaymentsReported: event.target.value })} /></label>
            <label className="recon-file">Estado de cuenta privado<input key={fileInputKey} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(event) => setDocumentFile(event.target.files?.[0] ?? null)} /><small>PDF o imagen, máximo 10 MB. Solo Juan Carlos y Ximena.</small></label>
            <label className="recon-notes">Notas<textarea rows={3} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Cambios observados, referencia bancaria o aclaraciones" /></label>
          </div>

          {variance != null && (
            <div className={`recon-variance ${variance === 0 ? 'is-zero' : variance > 0 ? 'is-higher' : 'is-lower'}`}>
              <span>Diferencia contra la proyección previa</span>
              <strong>{currency.format(variance)}</strong>
            </div>
          )}

          <button className="recon-primary" type="submit" disabled={busy}>{busy ? 'Procesando…' : 'Guardar borrador'}</button>
          {message && <p className="recon-message" role="status">{message}</p>}
        </form>

        <aside className="recon-schedule">
          <div className="recon-card-heading">
            <div><small>Tabla publicada</small><h2>Próximas mensualidades</h2></div>
          </div>
          <div className="recon-installments">
            {installments.slice(0, 8).map((item) => (
              <article key={item.id}>
                <time>{formatDate(item.due_date)}</time>
                <div><strong>Periodo {item.period_number}</strong><span>{item.charge_status === 'scheduled' ? 'Programado' : 'Registrado'}</span></div>
                <b>{currency.format(Number(item.actual_charge ?? item.scheduled_total ?? 0))}</b>
              </article>
            ))}
          </div>
          <p>Al aplicar un estado, el saldo y la siguiente mensualidad se actualizan para ambos integrantes y para el portal acreedor. El archivo permanece privado.</p>
        </aside>
      </div>

      <section className="recon-history">
        <div className="recon-card-heading">
          <div><small>Auditoría</small><h2>Historial de conciliaciones</h2></div>
          <span>{versions.length} versiones conservadas</span>
        </div>

        <div className="recon-history-list">
          {reconciliations.map((item) => {
            const itemVariance = Number(item.reported_balance) - Number(item.scheduled_balance ?? item.reported_balance);
            return (
              <article key={item.id}>
                <div className="recon-history-main">
                  <span className={`recon-status recon-status--${item.status}`}>{statusLabel(item.status)}</span>
                  <div><strong>{item.period_label || formatDate(item.statement_on)}</strong><small>Estado del {formatDate(item.statement_on)}</small></div>
                </div>
                <div className="recon-history-amounts">
                  <span>Saldo HSBC<strong>{currency.format(Number(item.reported_balance))}</strong></span>
                  <span>Variación<strong>{currency.format(itemVariance)}</strong></span>
                  <span>Próximo pago<strong>{item.next_payment_total != null ? currency.format(Number(item.next_payment_total)) : 'Sin dato'}</strong></span>
                </div>
                <div className="recon-history-actions">
                  {item.document_path && <button type="button" onClick={() => void openDocument(item)}>Abrir documento</button>}
                  {item.status === 'draft' && <button className="is-apply" type="button" disabled={busy} onClick={() => void applyReconciliation(item)}>Aplicar conciliación</button>}
                </div>
              </article>
            );
          })}
          {!reconciliations.length && <p className="recon-empty">Todavía no existen estados de cuenta conciliados.</p>}
        </div>
      </section>
    </section>
  );
}
